import { ApexVariant, Client, ClientInput, StrategyKey } from '../../api/types'
import { normalizePersonalNumber } from '../../validation'

/** Režim formuláře: nový klient (plný formulář) vs. stávající (lookup + předvyplnění). */
export type ClientMode = 'NEW' | 'EXISTING'

/** Stav polí klienta — všechna pole jako string (řízené inputy), převod až při odeslání. */
export interface ClientFormState {
  company: boolean
  companyName: string
  companyId: string
  titlePre: string
  firstName: string
  surname: string
  titleAfter: string
  personalNumber: string
  birthDate: string
  birthPlace: string
  street: string
  town: string
  zipCode: string
  contactStreet: string
  contactTown: string
  contactZip: string
  identityCardNumber: string
  identityCardValidity: string
  identityCardIssuer: string
  email: string
  phone: string
}

export const EMPTY_CLIENT_FORM: ClientFormState = {
  company: false,
  companyName: '',
  companyId: '',
  titlePre: '',
  firstName: '',
  surname: '',
  titleAfter: '',
  personalNumber: '',
  birthDate: '',
  birthPlace: '',
  street: '',
  town: '',
  zipCode: '',
  contactStreet: '',
  contactTown: '',
  contactZip: '',
  identityCardNumber: '',
  identityCardValidity: '',
  identityCardIssuer: '',
  email: '',
  phone: '',
}

/** Stav apexové části (investice). Částky drží text — parsují se při validaci. */
export interface ApexFormState {
  strategy: StrategyKey | ''
  variant: ApexVariant | ''
  amountText: string
  accountNumber: string
  entryFeePercentText: string
  entryFeeMode: 'INCREASE' | 'DEDUCT' | 'NONE'
}

export const EMPTY_APEX_FORM: ApexFormState = {
  strategy: '',
  variant: '',
  amountText: '',
  accountNumber: '',
  entryFeePercentText: '0',
  entryFeeMode: 'INCREASE',
}

/** Předvyplnění formuláře ze stávajícího klienta. */
export function clientToForm(client: Client): ClientFormState {
  return {
    company: client.company ?? false,
    companyName: client.companyName ?? '',
    companyId: client.companyId ?? '',
    titlePre: client.titlePre ?? '',
    firstName: client.firstName ?? '',
    surname: client.surname ?? '',
    titleAfter: client.titleAfter ?? '',
    personalNumber: client.personalNumber ?? '',
    birthDate: client.birthDate ?? '',
    birthPlace: client.birthPlace ?? '',
    street: client.street ?? '',
    town: client.town ?? '',
    zipCode: client.zipCode ?? '',
    contactStreet: client.contactStreet ?? '',
    contactTown: client.contactTown ?? '',
    contactZip: client.contactZip ?? '',
    identityCardNumber: client.identityCardNumber ?? '',
    identityCardValidity: client.identityCardValidity ?? '',
    identityCardIssuer: client.identityCardIssuer ?? '',
    email: client.email ?? '',
    phone: client.phone ?? '',
  }
}

/** Převod formuláře na DTO: trim, prázdné → undefined, RČ normalizované. */
export function formToClientInput(form: ClientFormState): ClientInput {
  const opt = (value: string): string | undefined => {
    const trimmed = value.trim()
    return trimmed ? trimmed : undefined
  }
  return {
    company: form.company,
    companyName: opt(form.companyName),
    companyId: opt(form.companyId),
    titlePre: opt(form.titlePre),
    firstName: opt(form.firstName),
    surname: opt(form.surname),
    titleAfter: opt(form.titleAfter),
    personalNumber: form.personalNumber.trim()
      ? normalizePersonalNumber(form.personalNumber)
      : undefined,
    birthDate: opt(form.birthDate),
    birthPlace: opt(form.birthPlace),
    street: opt(form.street),
    town: opt(form.town),
    zipCode: opt(form.zipCode),
    contactStreet: opt(form.contactStreet),
    contactTown: opt(form.contactTown),
    contactZip: opt(form.contactZip),
    identityCardNumber: opt(form.identityCardNumber),
    identityCardValidity: opt(form.identityCardValidity),
    identityCardIssuer: opt(form.identityCardIssuer),
    email: opt(form.email),
    phone: opt(form.phone),
  }
}
